/**
 * Region mapping: maps a lead's country code to the correct sending domain.
 * Each region's domain can ONLY send to contacts discovered in that region.
 *
 * 6 Domains:
 *   lawhelper.lat    → Latin America
 *   lawhelper.us     → North America (US + CA)
 *   lawhelper.eu     → Europe
 *   lawhelper.asia   → Asia-Pacific
 *   lawhelper.ae     → Middle East
 *   lawhelper.africa → Africa
 */

// Country → Region domain mapping
const COUNTRY_TO_REGION: Record<string, string> = {
    // Latin America
    MX: 'lawhelper.lat', BR: 'lawhelper.lat', AR: 'lawhelper.lat',
    CO: 'lawhelper.lat', CL: 'lawhelper.lat', PE: 'lawhelper.lat',
    UY: 'lawhelper.lat', EC: 'lawhelper.lat', VE: 'lawhelper.lat',
    CR: 'lawhelper.lat', PA: 'lawhelper.lat', DO: 'lawhelper.lat',
    GT: 'lawhelper.lat', HN: 'lawhelper.lat', SV: 'lawhelper.lat',
    BO: 'lawhelper.lat', PY: 'lawhelper.lat', NI: 'lawhelper.lat',
    CU: 'lawhelper.lat',
    LAT: 'lawhelper.lat',

    // North America
    US: 'lawhelper.us', CA: 'lawhelper.us',

    // Europe
    GB: 'lawhelper.eu', DE: 'lawhelper.eu', FR: 'lawhelper.eu',
    ES: 'lawhelper.eu', IT: 'lawhelper.eu', NL: 'lawhelper.eu',
    PT: 'lawhelper.eu', BE: 'lawhelper.eu', CH: 'lawhelper.eu',
    AT: 'lawhelper.eu', SE: 'lawhelper.eu', NO: 'lawhelper.eu',
    DK: 'lawhelper.eu', FI: 'lawhelper.eu', IE: 'lawhelper.eu',
    PL: 'lawhelper.eu', CZ: 'lawhelper.eu', RO: 'lawhelper.eu',
    EU: 'lawhelper.eu',

    // Asia-Pacific
    CN: 'lawhelper.asia', JP: 'lawhelper.asia', KR: 'lawhelper.asia',
    SG: 'lawhelper.asia', AU: 'lawhelper.asia', NZ: 'lawhelper.asia',
    IN: 'lawhelper.asia', TH: 'lawhelper.asia', VN: 'lawhelper.asia',
    MY: 'lawhelper.asia', ID: 'lawhelper.asia', PH: 'lawhelper.asia',
    HK: 'lawhelper.asia', TW: 'lawhelper.asia',
    ASIA: 'lawhelper.asia',

    // Middle East
    AE: 'lawhelper.ae', SA: 'lawhelper.ae', QA: 'lawhelper.ae',
    KW: 'lawhelper.ae', BH: 'lawhelper.ae', OM: 'lawhelper.ae',
    JO: 'lawhelper.ae', LB: 'lawhelper.ae', IL: 'lawhelper.ae',
    EG: 'lawhelper.ae', IQ: 'lawhelper.ae',

    // Africa
    ZA: 'lawhelper.africa', NG: 'lawhelper.africa', KE: 'lawhelper.africa',
    GH: 'lawhelper.africa', TZ: 'lawhelper.africa', ET: 'lawhelper.africa',
    UG: 'lawhelper.africa', RW: 'lawhelper.africa', SN: 'lawhelper.africa',
    CI: 'lawhelper.africa', CM: 'lawhelper.africa', MA: 'lawhelper.africa',
    TN: 'lawhelper.africa', DZ: 'lawhelper.africa', MU: 'lawhelper.africa',
    BW: 'lawhelper.africa',
    AF: 'lawhelper.africa',
};

const REGION_LABELS: Record<string, string> = {
    'lawhelper.lat': '🌎 拉丁美洲',
    'lawhelper.us': '🇺🇸 北美洲',
    'lawhelper.eu': '🇪🇺 欧洲',
    'lawhelper.asia': '🌏 亚太地区',
    'lawhelper.ae': '🕌 中东',
    'lawhelper.africa': '🌍 非洲',
};

export function getRegionDomain(country: string): string | null {
    return COUNTRY_TO_REGION[country] || null;
}

export function getRegionLabel(domain: string): string {
    return REGION_LABELS[domain] || domain;
}

export function getCommonRegion(countries: string[]): string | null {
    const domains = countries.map(c => getRegionDomain(c)).filter(Boolean);
    if (domains.length === 0) return null;
    const unique = Array.from(new Set(domains));
    return unique.length === 1 ? unique[0] : null;
}

export function getRegions(countries: string[]): string[] {
    return Array.from(new Set(countries.map(c => getRegionDomain(c)).filter(Boolean))) as string[];
}
